import type {
    ActivityOverviewSupportDto,
    SupportedFitnessApp
} from '../../../../core/api/FitnessActivityClient';
import { ProviderSeriesData, ProviderDataPoint } from './ActivityChart.types';
import {
    activityLabels,
    activityColorPalettes,
    providerLabels
} from './ActivityChart.constants';

export function extractAvailableData(apiResponse: ActivityOverviewSupportDto) {
    const providers = new Set<SupportedFitnessApp>();

    const activities = new Set<string>();

    apiResponse.dailySteps.forEach((step) => {
        if (step.fitnessApp) {
            providers.add(step.fitnessApp);
        }
        step.chunks.forEach((chunk) => {
            activities.add(chunk.stepsChunkType);
        });
    });
    return {
        availableProviders: Array.from(providers),
        availableActivities: Array.from(activities)
    };
}

export function transformToProviderSeries(
    apiResponse: ActivityOverviewSupportDto,
    offsetMinutes: number = 0
): ProviderSeriesData[] {
    const seriesList: ProviderSeriesData[] = [];
    const allTimestamps = new Set<number>();
    const providerActivityMap = new Map<
        string,
        Map<string, { time: number; value: number }[]>
    >();

    const offsetMs = offsetMinutes * 60_000;
    const utcMidnight = new Date(apiResponse.userDay + 'T00:00:00Z');
    const dayStart = new Date(utcMidnight.getTime());
    const dayEnd = new Date(utcMidnight.getTime() + 24 * 3_600_000 - 1);

    allTimestamps.add(dayStart.getTime());
    allTimestamps.add(dayEnd.getTime());

    apiResponse.dailySteps.forEach((dailyStep) => {
        const provider = dailyStep.fitnessApp;
        if (!provider) return;

        if (!providerActivityMap.has(provider)) {
            providerActivityMap.set(provider, new Map());
        }
        const activityMap = providerActivityMap.get(provider)!;

        dailyStep.chunks.forEach((chunk) => {
            const activityType = chunk.stepsChunkType;
            if (!activityMap.has(activityType)) {
                activityMap.set(activityType, []);
            }

            const endTime = new Date(chunk.timeRange.from).getTime() + offsetMs;
            allTimestamps.add(endTime);

            activityMap.get(activityType)!.push({
                time: endTime,
                value: chunk.stepCount
            });
        });
    });

    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);

    providerActivityMap.forEach((activityMap, provider) => {
        activityMap.forEach((chunks, activityType) => {
            const incrementMap = new Map<number, number>();
            chunks.forEach((chunk) => {
                const current = incrementMap.get(chunk.time) || 0;
                incrementMap.set(chunk.time, current + chunk.value);
            });

            let runningTotal = 0;

            const alignedData: ProviderDataPoint[] = sortedTimestamps.map(
                (timestamp) => {
                    if (incrementMap.has(timestamp)) {
                        runningTotal += incrementMap.get(timestamp)!;
                    }
                    return {
                        time: new Date(timestamp),
                        value: runningTotal
                    };
                }
            );

            seriesList.push({
                provider: provider as SupportedFitnessApp,
                activityType,
                data: alignedData
            });
        });
    });

    return seriesList;
}

export const getActivityLabel = (activity: string) => {
    return (
        activityLabels[activity] ||
        activity.charAt(0) + activity.slice(1).toLowerCase()
    );
};

export const getActivityColor = (activity: string) => {
    const palette = activityColorPalettes[activity];
    return palette ? palette[0] : '#9ca3af';
};

export const getProviderLabel = (provider: SupportedFitnessApp) => {
    return providerLabels[provider] || provider;
};

export const getProviderColor = (
    activityType: string,
    providerIndex: number
) => {
    const palette =
        activityColorPalettes[activityType] || activityColorPalettes['WALKING'];
    return palette[providerIndex % palette.length];
};
